/**
 * Slack settings section.
 *
 * Gated by the `slack_sync` feature flag. Renders the Slack integration
 * and handles Slack OAuth callback banners from query params.
 */

import { CheckCircle, XCircle } from '@untitledui/icons';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import SlackIntegration from '@/components/SlackIntegration';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  DEFAULT_SLACK_ERROR_MESSAGE,
  SLACK_ERROR_MESSAGES,
} from '@/lib/integrations/slack-errors';
import { useViewHealthMarker } from '@/lib/view-health';

export function SlackSection() {
  const isSlackEnabled = useFeatureFlag('slack_sync');
  const [searchParams, setSearchParams] = useSearchParams();
  useViewHealthMarker('settings-section-ready', { ready: isSlackEnabled });

  const [slackSuccess, setSlackSuccess] = useState(false);
  const [slackError, setSlackError] = useState<string | null>(null);

  useEffect(() => {
    const success = searchParams.get('slack_success');
    const error = searchParams.get('slack_error');

    if (success === 'true') {
      setSlackSuccess(true);
      setSlackError(null);
    } else {
      setSlackSuccess(false);
    }

    if (error) {
      setSlackError(error);
      setSlackSuccess(false);
      if (!SLACK_ERROR_MESSAGES[error]) {
        console.warn('Unknown Slack OAuth error code:', error);
      }
    } else {
      setSlackError(null);
    }
  }, [searchParams]);

  const dismissSlackSuccess = useCallback(() => {
    setSlackSuccess(false);
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.delete('slack_success');
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const dismissSlackError = useCallback(() => {
    setSlackError(null);
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.delete('slack_error');
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  if (!isSlackEnabled) {
    return null;
  }

  return (
    <div className='space-y-8'>
      <div>
        <h2 className='text-lg font-semibold text-fg-primary'>Slack</h2>
        <p className='text-sm text-fg-tertiary mt-0.5'>
          Connect your Slack workspace.
        </p>
      </div>

      <div className='space-y-6'>
        {/* Slack Success Banner */}
        {slackSuccess && (
          <div className='bg-utility-success-50 p-4 rounded-lg border border-utility-success-200'>
            <div className='flex items-start gap-3'>
              <CheckCircle className='w-5 h-5 text-utility-success-600 mt-0.5 shrink-0' />
              <div className='flex-1'>
                <p className='text-sm font-semibold text-utility-success-900'>
                  Slack workspace connected successfully
                </p>
                <p className='text-sm text-utility-success-700 mt-1'>
                  Your Slack workspace is now connected to capture meeting
                  notes.
                </p>
              </div>
              <button
                onClick={dismissSlackSuccess}
                className='flex-shrink-0 text-utility-success-600 hover:text-utility-success-700'
              >
                <XCircle className='w-5 h-5' />
              </button>
            </div>
          </div>
        )}

        {/* Slack Error Banner */}
        {slackError && (
          <div className='bg-utility-error-50 p-4 rounded-lg border border-utility-error-200'>
            <div className='flex items-start gap-3'>
              <XCircle className='w-5 h-5 text-utility-error-600 mt-0.5 shrink-0' />
              <div className='flex-1'>
                <p className='text-sm font-semibold text-utility-error-900'>
                  We couldn't connect Slack
                </p>
                <p className='text-sm text-utility-error-700 mt-1'>
                  {SLACK_ERROR_MESSAGES[slackError] ??
                    DEFAULT_SLACK_ERROR_MESSAGE}
                </p>
              </div>
              <button
                onClick={dismissSlackError}
                className='flex-shrink-0 text-utility-error-600 hover:text-utility-error-700'
              >
                <XCircle className='w-5 h-5' />
              </button>
            </div>
          </div>
        )}

        {/* Slack Integration */}
        <SlackIntegration />
      </div>
    </div>
  );
}
